import { useEffect, useRef } from 'react';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import { Back, Chevron } from '@/modules/common/components/icons';
import { Sort } from '@/modules/common/components/icons/Sort';
import { Skeleton } from '@/modules/common/components/skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/modules/common/components/ui/DropdownMenu';
import useDeviceDetectClient from '@/modules/common/hooks/useDeviceDetectClient';
import { useQueryParams } from '@/modules/common/hooks/useQueryParams';
import { sendGTMEvent } from '@/modules/common/services/sendGTMEvent';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { ProductPdpInterface } from '@/modules/product/models/product-pdp.interface';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { DATA_LAYER_EVENTS } from '@/modules/product/services/data-layer-events/events';

import { ToggleGridLayout } from '../samples/ToggleGridLayout';

const FILTER_SKELETON_WIDTHS = ['w-14', 'w-20', 'w-24', 'w-16', 'w-28', 'w-20'];

export const CollectionFilterSkeleton = () => {
  return (
    <div className="mx-4 mb-6 mt-8 lg:mx-8" data-testid="collection-filter-skeleton">
      <div className="relative flex flex-1 flex-col justify-between overflow-hidden md:flex-row">
        <ul
          className="no-scrollbar flex gap-2 overflow-x-auto whitespace-nowrap pr-12 md:pr-0"
          aria-hidden="true"
        >
          {FILTER_SKELETON_WIDTHS.map((width, index) => (
            <li key={index}>
              <Skeleton className={`h-12 ${width} rounded-full`} />
            </li>
          ))}
        </ul>
        <div className="pointer-events-none absolute right-0 top-0 -mr-28 h-12 w-48 bg-linear-transparent-to-white lg:hidden" />

        <div className="mt-[14px] flex justify-center gap-1 md:mt-0 md:flex-1 md:justify-end">
          <Skeleton className="h-10 w-10 rounded-md md:hidden" />
          <Skeleton className="h-10 w-12 rounded-md md:w-28" />
        </div>
      </div>
    </div>
  );
};

type CollectionFilter = {
  collection: ContentfulCollection;
  products: ProductPdpInterface[];
};
export const CollectionFilter = ({ products }: CollectionFilter) => {
  const [activeCategory, setActiveCategory] = useQueryParams<string>('c', 'All');
  const [sort, setSort] = useQueryParams<string>('sort', '');
  const { isMobile } = useDeviceDetectClient();
  const scrollBarRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (scrollBarRef.current) {
      const activeItem = scrollBarRef.current.querySelector(
        `[data-testid="collection-filter-${activeCategory}"]`
      );
      if (activeItem && isMobile) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      } else {
        scrollBarRef.current.scrollLeft = 0;
      }
    }
  }, [activeCategory, isMobile]);

  const handleCategoryClick = (option: string) => {
    const previouslyActiveCategory = activeCategory;
    setActiveCategory(option, { replaceHistory: true });

    if (previouslyActiveCategory !== 'All' && previouslyActiveCategory !== option) {
      const removeEventData: Record<string, unknown> = {
        feature: DataLayerFeature.Collections,
        where: DataLayerWhere.TopOfList,
        applied_filters: [{ type: 'Category', value: previouslyActiveCategory }],
      };

      sendGTMEvent({
        event: DATA_LAYER_EVENTS.filterRemoved,
        eventData: removeEventData,
      });
    }

    if (option !== 'All') {
      const applyEventData: Record<string, unknown> = {
        feature: DataLayerFeature.Collections,
        where: DataLayerWhere.TopOfList,
        applied_filters: [{ type: 'Category', value: option }],
      };

      sendGTMEvent({
        event: DATA_LAYER_EVENTS.filterApplied,
        eventData: applyEventData,
      });
    } else if (previouslyActiveCategory !== 'All') {
      const removeEventData: Record<string, unknown> = {
        feature: DataLayerFeature.Collections,
        where: DataLayerWhere.TopOfList,
        applied_filters: [{ type: 'Category', value: previouslyActiveCategory }],
      };

      sendGTMEvent({
        event: DATA_LAYER_EVENTS.filterRemoved,
        eventData: removeEventData,
      });
    }
  };

  const productCategories =
    products
      .map((product) => {
        const splited = product?.category?.name.split(' > ') || [];
        return splited[splited.length - 1];
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (isMobile) {
          if (a === activeCategory) {
            return -1;
          }
          if (b === activeCategory) {
            return 1;
          }
        }

        return a.localeCompare(b);
      }) || [];

  const productCategoriesOnly = productCategories;

  const categoriesWithAll = ['All', ...productCategoriesOnly].filter(
    (value, index, self) => self.indexOf(value) === index
  );

  return (
    <div className="mx-4 mb-6 mt-8 lg:mx-8">
      <div className="relative flex flex-1 flex-col justify-between overflow-hidden md:flex-row">
        <ul
          className="no-scrollbar flex gap-2 overflow-x-auto whitespace-nowrap pr-12 md:pr-0"
          ref={scrollBarRef}
        >
          {categoriesWithAll.map((option) => (
            <li key={option}>
              <Button
                variant={activeCategory === option ? 'primary' : 'secondary'}
                onClick={() => handleCategoryClick(option)}
                data-testid={`collection-filter-${option}`}
              >
                {option}
              </Button>
            </li>
          ))}
        </ul>
        <div className="pointer-events-none absolute right-0 top-0 -mr-28 h-12 w-48 bg-linear-transparent-to-white lg:hidden" />

        <div className="mt-[14px] flex justify-center gap-1 md:mt-0 md:flex-1 md:justify-end">
          <ToggleGridLayout className="md:hidden" />
          <DropdownMenu>
            <DropdownMenuTrigger
              className="right-0 top-0 ml-1 flex items-center gap-2 md:absolute md:translate-y-0 lg:static"
              data-testid="dropdown-trigger"
            >
              <div className="h-4 w-[1px] bg-gray-200 lg:hidden" />
              <Button
                asChild
                variant="outlined-light"
                className="min-w-12 items-center gap-2 max-lg:px-0 max-md:px-2 md:flex"
                data-testid="sort-button"
              >
                <div>
                  <Sort className="lg:hidden" />
                  <span className="md:hidden lg:block">
                    Sort By <Chevron className="hidden lg:inline" />
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem
                className="cursor-pointer"
                checked={sort === 'asc'}
                onCheckedChange={() => setSort('asc')}
                data-testid="sort-asc"
              >
                Sample Price: Low
                <Back className="h-5 w-5 rotate-180" />
                High
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                className="cursor-pointer"
                checked={sort === 'desc'}
                onCheckedChange={() => setSort('desc')}
                data-testid="sort-desc"
              >
                Sample Price: High
                <Back className="h-5 w-5 rotate-180" />
                Low
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
